import styled from "styled-components";
import { skills } from "../../data/constants";
import {
  Section,
  Eyebrow,
  SectionTitle,
  SectionIntro,
  Head,
  Tag,
  Glass,
  Reveal,
  useReveal,
} from "../../studio/ui";

/* ============================================================
   SKILLS — Divine Futurism
   Grouped relics of the craft, set in frosted stained-glass.
============================================================ */

/* masonry layout: cards pack by height so tall groups (Web) don't
   leave gaps under short ones — keeps the grid even */
const Grid = styled.div`
  column-count: 4;
  column-gap: 18px;
  width: 100%;

  @media ${({ theme }) => theme.bp.lg} {
    column-count: 3;
  }
  @media ${({ theme }) => theme.bp.md} {
    column-count: 2;
  }
  @media ${({ theme }) => theme.bp.sm} {
    column-count: 1;
  }
`;

/* wrapper sits as the direct column child — keep it intact per column */
const Cell = styled.div`
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
  margin-bottom: 18px;
`;

const Card = styled(Glass)`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 26px 24px;
`;

const GroupTitle = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ${({ theme }) => theme.fonts.serif};
  font-size: 1.4rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.white};

  @media ${({ theme }) => theme.bp.sm} {
    font-size: 1.28rem;
  }
`;

const Marker = styled.span`
  flex: none;
  width: 9px;
  height: 9px;
  transform: rotate(45deg);
  border-radius: 1px;
  background: ${({ theme }) => theme.gold};
  box-shadow: 0 0 10px ${({ theme }) => theme.goldGlow},
    0 0 24px ${({ theme }) => theme.goldGlow};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

/* larger, more visible skill chips than the shared Tag default */
const SkillTag = styled(Tag)`
  font-size: 14px;
  padding: 8px 15px;
  letter-spacing: 0.02em;
`;

function SkillCard({ group, i }) {
  const [ref, shown] = useReveal();
  return (
    <Cell>
      <Reveal
        ref={ref}
        $in={shown}
        $dir={i % 2 === 0 ? "left" : "right"}
        $delay={(i % 3) * 0.06}
      >
        <Card>
          <GroupTitle>
            <Marker aria-hidden="true" />
            {group.title}
          </GroupTitle>
          <Chips>
            {group.skills.map((skill) => (
              <SkillTag key={skill}>{skill}</SkillTag>
            ))}
          </Chips>
        </Card>
      </Reveal>
    </Cell>
  );
}

export default function Skills() {
  return (
    <Section id="skills">
      <Head>
        <Eyebrow>Skills</Eyebrow>
        <SectionTitle>
          The <em>craft</em> &amp; tools
        </SectionTitle>
        <SectionIntro>
          A production-tested full-stack toolkit, honed across languages,
          frameworks, and the systems that hold them together.
        </SectionIntro>
      </Head>

      <Grid>
        {skills.map((group, i) => (
          <SkillCard key={group.title} group={group} i={i} />
        ))}
      </Grid>
    </Section>
  );
}
